/* ============================================================
   App.jsx — routes between login, kiosk and admin, and owns the
   session bootstrap plus the global error surface.
   ============================================================ */

import { Component, useEffect, useState } from 'react';
import { ConfirmHost, ErrorBanner, BusyOverlay, Loading, LOGO } from '../components/ui.jsx';
import { Login } from '../screens/Login.jsx';
import { KioskApp } from '../screens/Kiosk.jsx';
import { AdminApp } from '../screens/Admin.jsx';
import { auth, useAppMeta, dismissError, refreshIfStale } from '../store/store.js';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="stage center" style={{ flexDirection: 'column', gap: 16, padding: 30, textAlign: 'center' }}>
        <img src={LOGO} style={{ width: 70, height: 70, objectFit: 'contain' }} alt="SIF" />
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 24, color: 'var(--green-deep)' }}>Hoppsan, något gick fel</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 420, fontWeight: 600, wordBreak: 'break-word' }}>
          {String((this.state.err && this.state.err.message) || this.state.err)}
        </div>
        <button className="btn btn-primary btn-lg" onClick={() => location.reload()}>Ladda om appen</button>
      </div>
    );
  }
}

export function App() {
  const meta = useAppMeta();
  const [booted, setBooted] = useState(false);

  /* Restore an existing session so a page refresh does not log the kiosk out. */
  useEffect(() => {
    auth.probe().finally(() => setBooted(true));
  }, []);

  /* A tablet that has been asleep all afternoon should not show stale numbers. */
  useEffect(() => {
    const onFocus = () => refreshIfStale();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  /* The iPad keyboard slides over a fixed layout instead of resizing it, so a
     field near the bottom ends up hidden behind it. Pull it into view. */
  useEffect(() => {
    if (!window.matchMedia('(pointer: coarse)').matches) return;
    const onFocusIn = e => {
      if (!e.target.matches('input, textarea, select')) return;
      setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  const role = meta.session && meta.session.role;
  const logout = () => auth.logout();
  /* Första laddningen har sin egen helsida — överlägget är för allt därefter. */
  const booting = !booted || (role && meta.loading && !meta.loadedAt);

  return (
    <div className="viewport">
      <ErrorBoundary>
        {booting
          ? <Loading label={booted ? 'Hämtar kioskdata…' : 'Startar SIF Kiosk…'} />
          : !role ? <Login />
            : role === 'admin' ? <AdminApp onExit={logout} />
              : <KioskApp onExit={logout} />}
        <BusyOverlay active={!booting && meta.busy > 0} label={meta.busyLabel} />
        <ConfirmHost />
        <ErrorBanner message={meta.error} onDismiss={dismissError} />
      </ErrorBoundary>
    </div>
  );
}
